import { cva } from "class-variance-authority";
import { cn } from "./utils.js";

/**
 * Downtime UI accents — independent of fleet asset status colors in
 * `status-utils.js`.
 *
 * Duration tiers (closed events): short ≤1h, medium >1h and ≤24h, long >24h.
 * `ongoing` is a separate open-event indicator (no closed hours yet), shared by
 * RCA shutdown/history lists and downtime overview table/timeline.
 *
 * Initial tints may resemble status greens / oranges / brand-red / amber so the
 * UI stays consistent today, but changing asset status colors must not change
 * these entries (or vice versa).
 */
const DOWNTIME_DURATION_PILL = {
  short:
    "border-asset-running/40 bg-asset-running/10 text-green-700 hover:bg-asset-running/10 dark:border-asset-running/50 dark:bg-asset-running/15 dark:text-green-400 dark:hover:bg-asset-running/15",
  medium:
    "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-50 dark:border-orange-500/60 dark:bg-orange-500/15 dark:text-orange-300 dark:hover:bg-orange-500/15",
  long: "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/10 dark:border-destructive/50 dark:bg-destructive/15 dark:text-red-300 dark:hover:bg-destructive/15",
};

/** Open downtime event — not a length tier and not an asset status. */
const ONGOING_DOWNTIME_PILL =
  "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-50 dark:border-amber-500/60 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/15";

const DOWNTIME_DURATION_DOT = {
  short: "bg-asset-running dark:bg-green-400",
  medium: "bg-orange-400",
  long: "bg-red-500 dark:bg-red-400",
};

const ONGOING_DOWNTIME_DOT = "bg-amber-500 dark:bg-amber-400";

/** Inline duration labels (shutdown event list), not pill surfaces. */
const DOWNTIME_DURATION_TEXT = {
  short: "text-green-600 dark:text-green-400",
  medium: "text-orange-500 dark:text-orange-300",
  long: "text-red-600 dark:text-red-400",
};

const downtimeDurationBadgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
  {
    variants: {
      tier: {
        short: DOWNTIME_DURATION_PILL.short,
        medium: DOWNTIME_DURATION_PILL.medium,
        long: DOWNTIME_DURATION_PILL.long,
      },
    },
  }
);

const downtimeDurationDotVariants = cva(
  "inline-block h-2 w-2 shrink-0 rounded-full",
  {
    variants: {
      tier: {
        short: DOWNTIME_DURATION_DOT.short,
        medium: DOWNTIME_DURATION_DOT.medium,
        long: DOWNTIME_DURATION_DOT.long,
      },
    },
  }
);

const ONGOING_BADGE_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap";

/**
 * Map closed downtime hours to a duration tier.
 * @param {number} dtHours
 * @returns {"short"|"medium"|"long"}
 */
export function getDowntimeDurationTier(dtHours) {
  const h = Number(dtHours) || 0;
  if (h <= 1) return "short";
  if (h <= 24) return "medium";
  return "long";
}

/**
 * Pill surface classes for a closed downtime duration tier.
 * @param {"short"|"medium"|"long"} tier
 * @param {string} [className]
 * @returns {string}
 */
export function getDowntimeDurationBadgeClass(tier, className) {
  return cn(downtimeDurationBadgeVariants({ tier }), className);
}

/**
 * Dot classes for a closed downtime duration tier.
 * @param {"short"|"medium"|"long"} tier
 * @param {string} [className]
 * @returns {string}
 */
export function getDowntimeDurationDotClass(tier, className) {
  return cn(downtimeDurationDotVariants({ tier }), className);
}

/**
 * Shared “Ongoing” pill for open downtime events (RCA + downtime pages).
 * @param {string} [className]
 * @returns {string}
 */
export function getOngoingDowntimeBadgeClass(className) {
  return cn(ONGOING_BADGE_BASE, ONGOING_DOWNTIME_PILL, className);
}

/**
 * Dot for an open downtime “Ongoing” pill.
 * @param {string} [className]
 * @returns {string}
 */
export function getOngoingDowntimeDotClass(className) {
  return cn(
    "inline-block h-2 w-2 shrink-0 rounded-full",
    ONGOING_DOWNTIME_DOT,
    className
  );
}

/**
 * Dot + duration text classes for shutdown list rows (not pill chrome).
 * @param {number} dtHours
 * @returns {{ tier: "short"|"medium"|"long", dot: string, duration: string }}
 */
export function getDowntimeDurationSeverityClasses(dtHours) {
  const tier = getDowntimeDurationTier(dtHours);
  return {
    tier,
    dot: DOWNTIME_DURATION_DOT[tier],
    duration: DOWNTIME_DURATION_TEXT[tier],
  };
}
